import moment from 'moment';
import { ConfigConstants } from '../../../config/config-constants';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { isDefined } from '../../../utilities/is-defined';
import { TimesheetAddressState } from '../../address';
import { NotificationIdState } from '../../utilities/notification-id.type';
import {
    createTimesheetState,
    TimesheetEntryState,
    TimesheetManualEntryState,
    TimesheetState,
    TimesheetWorkEntryState,
} from '../timesheet-collection.state';
import { withSortedEntries } from './sorted-entries';

/**
 * Converts a timesheet the legacy `StorageTimesheetService` wrote (`JSON.stringify(TimesheetModel)`
 * under `currentTimesheet` / `submittedTimesheet`) into the slice's flat shape — the inverse of
 * `toJsonTimesheetModel`, used once per device by the legacy-data migration.
 *
 * Works on the parsed JSON rather than going through `TimesheetModelFromJson`: the legacy models
 * pull config, value helpers and translations out of the injector while they are constructed, and
 * none of that bears on the values a stored timesheet carries. The shapes below describe what
 * `JSON.stringify` actually produced over the models' lifetime, which is looser than the `Json*`
 * interfaces claim:
 * - a `moment` serializes to its ISO string, and an invalid one (an unset end of work) to `null`;
 * - an entry `distance` is a serialized `TextModel` (`{ name, value, options }`) or `''`;
 * - an address field is a plain string, or — for addresses written before
 *   `TimesheetAddressModel.toJSON` existed — a serialized `TextModel`, with the US state as a
 *   serialized dropdown (`TimesheetAddressModelFromJson` accepts both, and so does this).
 *
 * Two legacy quirks carry over deliberately:
 * - `TimesheetEntryModel.toJSON` writes `endTime: this.endDate.value`, so only `startDate` and
 *   `endDate` are read, as rehydration always did;
 * - the address keeps the legacy key `statesOfUSA`, which is renamed to the slice's `stateOfUSA`
 *   here. The address is otherwise left as the full projection: `normalizeStoredEntryAddresses`
 *   resolves it against the address slice exactly as it does for a slice written before entries
 *   stored address ids.
 *
 * Expenses are dropped: the slice has no expenses, and `toJsonTimesheetModel` always hands the
 * legacy models an empty list.
 */

type StoredText = string | { value?: string } | null | undefined;

type StoredAddress = {
    street?: StoredText;
    city?: StoredText;
    postCode?: StoredText;
    country?: StoredText;
    addrText?: StoredText;
    addrNumber?: StoredText;
    statesOfUSA?: string | { selectedEntries?: Array<{ model?: { value?: string } }> } | null;
    addrKey?: string;
};

type StoredNotificationId = { frontId?: string; qmNum?: string; maNum?: string };

type StoredWorkEntry = {
    notificationId?: StoredNotificationId;
    startDate?: string | null;
    endDate?: string | null;
    workCode?: string;
    activityType?: string;
    address?: StoredAddress | null;
    addressInOutModel?: boolean;
    distance?: StoredText;
    meansOfTransport?: string;
    isTravelBillable?: boolean;
    afterHourCallOut?: boolean;
};

type StoredNotification = {
    notificationId?: StoredNotificationId;
    timesheetEntries?: StoredWorkEntry[];
    distance?: StoredText;
    customerName?: string;
    isConfirmationSubmitted?: boolean;
};

type StoredManualEntry = {
    startDateTime?: string | null;
    endDateTime?: string | null;
    address?: StoredAddress | null;
    taskType?: string;
    isTravelBillable?: boolean;
    addressInOutModel?: boolean;
    notificationId?: StoredNotificationId;
    travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
};

/** A timesheet as the legacy storage wrote it; every field may be missing from an old write. */
export type StoredLegacyTimesheet = {
    startOfWork?: string | null;
    endOfWork?: string | null;
    notifications?: StoredNotification[];
    manualTimesheetEntries?: StoredManualEntry[];
    attendanceType?: ConfigConstants.AttendanceType;
    startAddressValue?: string;
    endAddressValue?: string;
    saveTimestamp?: number;
    isRemoteTimesheet?: boolean;
};

/** What {@link fromJsonTimesheetModel} produces: the timesheet, and what it had to leave out. */
export type ConvertedLegacyTimesheet = {
    timesheet: TimesheetState;
    /** One line per entry dropped for an unreadable start or end, for the caller to log. */
    warnings: string[];
};

const textValue = (text: StoredText): string => {
    if (!isDefined(text)) {
        return '';
    }
    return typeof text === 'object' ? (text.value ?? '') : text;
};

/**
 * @param value a stored instant
 * @returns the instant as an ISO timestamp, or `undefined` when it is missing or unreadable
 */
const isoInstant = (value: string | null | undefined): string | undefined => {
    if (!value) {
        return undefined;
    }
    const instant = moment(value);
    return instant.isValid() ? instant.toISOString() : undefined;
};

const numericDistance = (distance: StoredText): number => {
    const value = Number(textValue(distance));
    return Number.isFinite(value) && value > 0 ? value : 0;
};

const notificationIdOf = (id: StoredNotificationId | undefined): NotificationIdState => ({
    frontId: id?.frontId ?? '',
    qmNum: id?.qmNum ?? '',
    maNum: id?.maNum ?? '',
});

const notificationKey = (id: NotificationIdState): string => `${id.frontId}:${id.qmNum}:${id.maNum}`;

/**
 * The legacy address as the full projection `normalizeStoredEntryAddresses` resolves. Typed as the
 * id the entry will hold once normalized, because that is what `address` is declared as; the
 * normalizer widens it back to what storage holds (see `PersistedEntryAddress` there).
 *
 * @param address the stored address, absent when the entry had none
 */
function legacyAddress(address: StoredAddress | null | undefined): string | null {
    if (!address) {
        return null;
    }
    const stateOfUSA =
        typeof address.statesOfUSA === 'object' && address.statesOfUSA !== null
            ? (address.statesOfUSA.selectedEntries?.[0]?.model?.value ?? '')
            : (address.statesOfUSA ?? '');
    const projection: TimesheetAddressState = {
        street: textValue(address.street),
        city: textValue(address.city),
        postCode: textValue(address.postCode),
        country: textValue(address.country),
        addrText: textValue(address.addrText),
        addrNumber: textValue(address.addrNumber),
        ...(stateOfUSA ? { stateOfUSA } : {}),
        ...(address.addrKey ? { addrKey: address.addrKey } : {}),
        allowToHide: false,
        id: '',
    };
    return projection as unknown as string;
}

/**
 * The work entries of one legacy notification block. The block's `distance` is the one the
 * confirmation reported; `toJsonTimesheetModel` writes it onto the group's first entry with a
 * distance, so when no entry carries one of its own it lands on the block's first entry here.
 *
 * @param notification the legacy notification block
 * @param idPrefix the prefix that makes the entry ids unique within the timesheet
 * @param warnings collects the entries dropped for an unreadable start or end
 */
function workEntriesOf(
    notification: StoredNotification,
    idPrefix: string,
    warnings: string[],
): TimesheetWorkEntryState[] {
    const notificationId = notificationIdOf(notification.notificationId);
    const entries: TimesheetWorkEntryState[] = [];
    (notification.timesheetEntries ?? []).forEach((stored, index) => {
        const start = isoInstant(stored.startDate);
        const end = isoInstant(stored.endDate);
        if (!start || !end) {
            warnings.push(
                `work entry ${index} of notification ${notificationKey(notificationId)} has no readable start or end`,
            );
            return;
        }
        entries.push({
            kind: 'work',
            entryId: `${idPrefix}-work-${notificationKey(notificationId)}-${index}`,
            notificationId: stored.notificationId ? notificationIdOf(stored.notificationId) : notificationId,
            start,
            end,
            address: legacyAddress(stored.address),
            addressInOutModel: stored.addressInOutModel ?? true,
            isTravelBillable: stored.isTravelBillable ?? false,
            workCode: stored.workCode ?? '',
            activityType: stored.activityType ?? '',
            meansOfTransport: stored.meansOfTransport ?? '',
            distance: numericDistance(stored.distance),
            afterHourCallOut: stored.afterHourCallOut ?? false,
            customerName: notification.customerName ?? '',
            // The legacy block never stored it; `Unknown` is what the slice falls back to for a
            // notification it cannot resolve either.
            notificationType: ConfigConstants.NotificationTypes.Unknown,
            // Never stored by the legacy models either, so the row shows no short-confirmation label.
            confirmationType: null,
            // `TimesheetNotificationModel` defaults the flag to `true`, so an absent one means submitted.
            isConfirmationSubmitted: notification.isConfirmationSubmitted ?? true,
        });
    });

    const blockDistance = numericDistance(notification.distance);
    if (blockDistance > 0 && entries.length > 0 && !entries.some((entry) => entry.distance > 0)) {
        entries[0] = { ...entries[0], distance: blockDistance };
    }
    return entries;
}

function manualEntryOf(
    stored: StoredManualEntry,
    index: number,
    idPrefix: string,
    warnings: string[],
): TimesheetManualEntryState | undefined {
    const start = isoInstant(stored.startDateTime);
    const end = isoInstant(stored.endDateTime);
    if (!start || !end) {
        warnings.push(`manual entry ${index} has no readable start or end`);
        return undefined;
    }
    return {
        kind: 'manual',
        entryId: `${idPrefix}-manual-${index}`,
        notificationId: notificationIdOf(stored.notificationId),
        start,
        end,
        address: legacyAddress(stored.address),
        addressInOutModel: stored.addressInOutModel ?? true,
        isTravelBillable: stored.isTravelBillable ?? false,
        taskType: stored.taskType ?? '',
        ...(stored.travelType === undefined ? {} : { travelType: stored.travelType }),
    };
}

/**
 * Whether a legacy timesheet carries anything worth migrating. The legacy facade writes an empty
 * `TimesheetModel` under `currentTimesheet` after every timesheet submit, so a present key alone does
 * not mean there is data.
 *
 * @param stored the parsed legacy timesheet
 */
export function hasLegacyTimesheetEntries(stored: StoredLegacyTimesheet): boolean {
    return (
        (stored.notifications ?? []).some((notification) => (notification.timesheetEntries ?? []).length > 0) ||
        (stored.manualTimesheetEntries ?? []).length > 0
    );
}

/**
 * @param stored the parsed legacy timesheet
 * @param timesheetId the identity the converted timesheet gets — a parameter so this stays pure
 * @param isSubmitted whether it was stored under `submittedTimesheet`; like `TimesheetModelFromJson`,
 *     the key decides, not the stored flag
 * @returns the timesheet in the slice's shape, entries sorted, plus what had to be dropped
 */
export function fromJsonTimesheetModel(
    stored: StoredLegacyTimesheet,
    timesheetId: string,
    isSubmitted: boolean,
): ConvertedLegacyTimesheet {
    const warnings: string[] = [];
    // Entry ids are derived from the position in the stored timesheet, so converting the same
    // stored timesheet twice yields the same ids.
    const idPrefix = `legacy-${isSubmitted ? 'submitted' : 'current'}`;
    const workEntries = (stored.notifications ?? []).flatMap((notification) =>
        workEntriesOf(notification, idPrefix, warnings),
    );
    const manualEntries = (stored.manualTimesheetEntries ?? [])
        .map((entry, index) => manualEntryOf(entry, index, idPrefix, warnings))
        .filter(isDefined);
    const entries: TimesheetEntryState[] = [...workEntries, ...manualEntries];

    const timesheet: TimesheetState = {
        ...createTimesheetState(timesheetId),
        startOfWork: isoInstant(stored.startOfWork) ?? '',
        endOfWork: isoInstant(stored.endOfWork) ?? '',
        entries,
        isSubmitted,
        attendanceType: stored.attendanceType ?? ConfigConstants.AttendanceType.FREE,
        startAddressValue: stored.startAddressValue ?? '',
        endAddressValue: stored.endAddressValue ?? '',
        saveTimestamp: stored.saveTimestamp ?? 0,
        isRemoteTimesheet: stored.isRemoteTimesheet ?? false,
    };
    return { timesheet: withSortedEntries(timesheet), warnings };
}

/** What {@link mergeLegacyTimesheets} produces. */
export type MergedLegacyTimesheets = {
    timesheets: TimesheetState[];
    /** `false` when the slice already held everything the legacy timesheets carry. */
    changed: boolean;
};

/**
 * Folds the converted legacy timesheets into the slice's collection without ever overwriting what
 * the slice already holds — the migration can run again (a crash between persist and delete, or a
 * legacy key brought back by a download from a device still on the legacy path), and a re-run must
 * neither duplicate a timesheet nor replace one the technician has since worked on.
 *
 * - The legacy current timesheet takes the place of the open one only while that is still empty,
 *   and keeps the open one's `timesheetId`.
 * - The legacy submitted timesheet is appended unless a submitted one with the same start of work
 *   is already there.
 *
 * @param timesheets the slice's collection, as hydrated
 * @param legacyCurrent the converted `currentTimesheet`, `undefined` when there was none to migrate
 * @param legacySubmitted the converted `submittedTimesheet`, `undefined` when there was none to migrate
 */
export function mergeLegacyTimesheets(
    timesheets: readonly TimesheetState[],
    legacyCurrent: TimesheetState | undefined,
    legacySubmitted: TimesheetState | undefined,
): MergedLegacyTimesheets {
    let merged = [...timesheets];
    let changed = false;

    if (legacyCurrent) {
        const openIndex = merged.findIndex((timesheet) => !timesheet.isSubmitted);
        if (openIndex === -1) {
            merged = [{ ...legacyCurrent, isSubmitted: false }, ...merged];
            changed = true;
        } else if (merged[openIndex].entries.length === 0) {
            merged[openIndex] = { ...legacyCurrent, timesheetId: merged[openIndex].timesheetId, isSubmitted: false };
            changed = true;
        }
    }

    // Compared as instants: the slice and the converter may format the same start of work differently.
    const isSameStart = (timesheet: TimesheetState): boolean =>
        legacySubmitted?.startOfWork
            ? moment(timesheet.startOfWork).isSame(moment(legacySubmitted.startOfWork))
            : !timesheet.startOfWork;
    if (legacySubmitted && !merged.some((timesheet) => timesheet.isSubmitted && isSameStart(timesheet))) {
        merged = [...merged, { ...legacySubmitted, isSubmitted: true }];
        changed = true;
    }

    return { timesheets: changed ? merged : [...timesheets], changed };
}
